import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  AlertResponse,
  ApiError,
  AssistantLogListResponse,
  AuditListResponse,
  ConsentResponse,
  CreateTenantResponse,
  DocumentDownloadUrlResponse,
  DocumentRequestResponse,
  DocumentUploadUrlResponse,
  DueRenewalResponse,
  EntityMode,
  ExpedienteActivityResponse,
  ExpedienteCanal,
  ExpedienteProducto,
  UpdateExpedienteInput,
  OutreachListResponse,
  OutreachMessageResponse,
  RunOutreachResponse,
  RunRenewalsResponse,
  ScreeningBatchResponse,
  RiskMatrixResponse,
  RiskMatrixConfig,
  TenantSettingsResponse,
  AssistantResponse,
  AssistantChatResponse,
  AssistantExecuteResponse,
  AssistantToolsResponse,
  ExpedienteDocumentResponse,
  ExpedienteResponse,
  ExpedienteVersionDetailResponse,
  ExpedienteVersionsResponse,
  MeResponse,
  OcrPrefillResult,
  OwnerResponse,
  OwnerRol,
  OwnerPosicion,
  RegistryOfficerResponse,
  ReviewResponse,
  RoleResponse,
  SubjectResponse,
  TenantResponse,
  UpdateOutreachInput,
  UserResponse,
  ValidationResult,
  VinculoResponse,
} from '@cumpli/shared';
import { ALLOWED_DOCUMENT_MIME_TYPES, OCR_PREFILL_MAX_FILE_BASE64_CHARS } from '@cumpli/shared';
import type {
  RpConsultaInput,
  RpConsultaResult,
  RpWatch,
  RpWeeklyCheckResult,
  RpLoginInput,
  RpSalud,
  RpSweepResult,
} from './registro';
import { DEMO, demoApi } from './demo';
import { RequestError } from './request-error';
import { log } from './log';
import type {
  Alert,
  AlertPatch,
  Consent,
  ConsentPatch,
  CreateConsentInput,
  CreateOwnerInput,
  CreateReviewInput,
  CreateSubjectInput,
  EstadoExpediente,
  Expediente,
  ExpedienteDoc,
  ExpedienteDocPatch,
  NewExpedienteInput,
  Owner,
  OwnerPatch,
  Review,
  ReviewPatch,
  RunValidationBody,
  Subject,
  SubjectPatch,
  ValidationRun,
} from './phase1';

// Re-exported so existing imports (`import { RequestError } from '../lib/api'`)
// keep working. The class lives in its own module to avoid an api<->demo cycle.
export { RequestError };

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

// A trashed (soft-deleted) expediente row. Same shape as the active response plus
// the deletion timestamp the papelera surfaces. `deletedAt` is optional so the
// type still holds if the backend rolls it out after this client.
export type PapeleraRow = ExpedienteResponse & { deletedAt?: string | null };

// Dev-auth (local backend only): when VITE_DEV_AUTH=true the client skips Amplify
// entirely and identifies itself with an `x-dev-sub` header the local dev server
// (packages/api/src/dev-server.ts) resolves to a seeded user's real tenant/roles.
// This exists ONLY so the real API can be driven on a laptop without Cognito; in
// any normal build VITE_DEV_AUTH is unset and the Amplify JWT path below runs.
export const DEV_AUTH: boolean = import.meta.env.VITE_DEV_AUTH === 'true';
const DEV_SUB: string = import.meta.env.VITE_DEV_SUB ?? 'demo-sub-ana-robles';

const apiLog = log.child('api');

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const startedAt = Date.now();
  // Per-call correlation id, sent as `x-request-id` so a browser log line can be
  // matched to the server-side request log for the same call (safe to log — I7).
  const requestId = crypto.randomUUID();
  // Log only the pathname, never the query string — search boxes (e.g. Users
  // search) put a person's name/email into `?search=...` (I7). The request still
  // uses the full `path`.
  const logPath = path.split('?')[0];
  let authHeaders: Record<string, string>;
  if (DEV_AUTH) {
    authHeaders = { 'x-dev-sub': DEV_SUB };
  } else {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      apiLog.warn('no session — request blocked', { method, path: logPath, requestId });
      throw new RequestError(401, 'UNAUTHORIZED', 'No session');
    }
    authHeaders = { authorization: `Bearer ${token}` };
  }

  apiLog.debug('→ request', { method, path: logPath, requestId });

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        ...authHeaders,
        'x-request-id': requestId,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    // Network/CORS failure — never reached the server.
    apiLog.error('✗ network error', { method, path: logPath, requestId, durationMs: Date.now() - startedAt });
    throw err;
  }

  const durationMs = Date.now() - startedAt;

  if (!res.ok) {
    let code = 'UNKNOWN';
    let message = res.statusText;
    try {
      const data = (await res.json()) as ApiError;
      code = data.error.code;
      message = data.error.message;
    } catch {
      // non-JSON error body
    }
    apiLog.warn('✗ response', { method, path: logPath, requestId, status: res.status, code, durationMs });
    throw new RequestError(res.status, code, message);
  }

  apiLog.info('✓ response', { method, path: logPath, requestId, status: res.status, durationMs });
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

// The UI uses the phase1 `Expediente` shape (name/started/analyst/review); the
// backend returns ExpedienteResponse (nombre/startedAt/assignedTo/nextReviewAt).
// Map at the boundary. Rich fields (docs/validations/factors/ubo/history) come
// from their own endpoints later — default to empty for now.
function toUiExpediente(r: ExpedienteResponse): Expediente {
  return {
    id: r.id,
    persona: r.persona,
    name: r.nombre,
    kind: r.kind,
    icon: r.persona === 'legal' ? '🏛️' : '🧑',
    docId: r.docId,
    sector: r.sector,
    email: r.email,
    phone: r.phone,
    dateOfBirth: r.dateOfBirth ?? null,
    direccion: r.direccion ?? null,
    canal: r.canal as ExpedienteCanal | null,
    producto: r.producto as ExpedienteProducto | null,
    docKind: r.docKind,
    folio: r.folio,
    constitutionDate: r.constitutionDate,
    // PJ physical address + PN FATCA/CRS declarations. paisResidenciaFiscal is
    // lowercased — the API stores uppercase ISO-2 (iso2CountrySchema
    // .toUpperCase()) while the UI catalogs (RISK_COUNTRY_RISK_CATALOG /
    // NACIONALIDAD_OPTS) key on lowercase ('pa').
    direccionFisica: r.direccionFisica ?? null,
    usPerson: r.usPerson ?? null,
    paisResidenciaFiscal: r.paisResidenciaFiscal ? r.paisResidenciaFiscal.toLowerCase() : null,
    multiplesNacionalidades: r.multiplesNacionalidades ?? null,
    started: r.startedAt,
    estado: r.estado as Expediente['estado'],
    riesgo: r.riesgo as Expediente['riesgo'],
    score: r.score,
    pep: r.pep,
    analyst: r.assignedTo,
    review: r.nextReviewAt,
    fields: [],
    docs: [],
    validations: [],
    factors: [],
    ubo: null,
    history: [],
  };
}

// --- Per-domain mappers (backend *Response -> clean camelCase UI object) ---

function toUiAlert(r: AlertResponse): Alert {
  return {
    id: r.id,
    tipo: r.alertType,
    cliente: r.title,
    expedienteId: r.expedienteId,
    prioridad: r.priority,
    estado: r.estado,
    asignada: r.assignedTo,
    subjectId: r.subjectId,
    descripcion: r.description,
    evidencia: r.evidence,
    abierta: r.openedAt,
    resuelta: r.resolvedAt,
    codigoResolucion: r.resolutionCode,
    notaResolucion: r.resolutionNote,
  };
}

// Fase 4 drill-down (req#7): the UI doc shape plus its owner pointer. Kept here
// as an additive boundary refinement instead of editing phase1.ts (frozen — PR
// #250 owns it). `ownerId` is null for an entity-level document, set for a
// per-person one; optional so the in-memory demo (which may omit it) still fits.
export type ExpedienteDocUi = ExpedienteDoc & { ownerId?: string | null };

function toUiDocument(r: ExpedienteDocumentResponse): ExpedienteDocUi {
  return {
    id: r.id,
    expedienteId: r.expedienteId,
    category: r.category,
    name: r.name,
    estado: r.estado as ExpedienteDoc['estado'],
    expiry: r.expiryDate,
    documentTypeId: r.documentTypeId,
    ownerId: r.ownerId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function toUiValidationRun(r: ValidationResult): ValidationRun {
  return {
    id: r.id,
    key: r.key,
    subjectType: r.subjectType,
    subjectId: r.subjectId,
    estado: r.status,
    summary: r.summary,
    evidence: r.evidence,
    providerRef: r.providerRef,
    ranAt: r.ranAt,
  };
}

function toUiSubject(r: SubjectResponse): Subject {
  return {
    id: r.id,
    expedienteId: r.expedienteId,
    subjectRef: r.subjectRef,
    subjectType: r.subjectType,
    declaredAddress: r.declaredAddress,
    sector: r.sector,
    activity: r.activity,
    requiredDocuments: r.requiredDocuments,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

// Fase 4 drill-down (req#7): the UI owner shape plus the Fase 1 per-person fields
// the persona view renders (rol, posición, % patrimonial, país de residencia
// fiscal, PEP/US person). Additive boundary refinement — phase1.ts's `Owner` is
// frozen (PR #250 owns it), so we widen at the mapper instead. All optional so a
// legacy row or the demo store (which omits them) still fits.
export type OwnerUi = Owner & {
  rol?: OwnerRol | null;
  posicion?: OwnerPosicion | null;
  paisResidenciaFiscal?: string | null;
  porcentajePatrimonial?: number | null;
  pepPropio?: boolean;
  pepFamiliar?: boolean;
  usPerson?: boolean;
};

function toUiOwner(r: OwnerResponse): OwnerUi {
  return {
    id: r.id,
    expedienteId: r.expedienteId,
    subjectRef: r.subjectRef,
    parentOwnerId: r.parentOwnerId,
    name: r.name,
    ownerType: r.ownerType,
    ownershipPct: r.ownershipPct,
    idNumber: r.idNumber,
    country: r.country,
    kycComplete: r.kycComplete,
    kycExpedienteId: r.kycExpedienteId,
    rol: r.rol ?? null,
    posicion: r.posicion ?? null,
    paisResidenciaFiscal: r.paisResidenciaFiscal ?? null,
    porcentajePatrimonial: r.porcentajePatrimonial ?? null,
    pepPropio: r.pepPropio ?? false,
    pepFamiliar: r.pepFamiliar ?? false,
    usPerson: r.usPerson ?? false,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function toUiReview(r: ReviewResponse): Review {
  return {
    id: r.id,
    expedienteId: r.expedienteId,
    reviewType: r.reviewType,
    riskLevel: r.riskLevel,
    cadenceMonths: r.cadenceMonths,
    scheduledFor: r.scheduledFor,
    status: r.status,
    assignedTo: r.assignedTo,
    completedAt: r.completedAt,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function toUiConsent(r: ConsentResponse): Consent {
  return {
    id: r.id,
    expedienteId: r.expedienteId,
    subjectRef: r.subjectRef,
    subjectType: r.subjectType,
    source: r.source,
    evidenceRef: r.evidenceRef,
    grantedAt: r.grantedAt,
    expiresAt: r.expiresAt,
    revokedAt: r.revokedAt,
    recordedBy: r.recordedBy,
    createdAt: r.createdAt,
  };
}

// --- OCR-prefill client-side size guard ---
// Lambda's whole-invocation event has a 6 MB hard cap; `OCR_PREFILL_MAX_FILE_
// BASE64_CHARS` (packages/shared) keeps the request body well under it. Images
// are downscaled client-side to fit; PDFs (which can't be) are rejected fast,
// before ever calling the API, if they're still too big.

/** Images at or under this size are sent as-is; larger ones are downscaled first. */
const OCR_PREFILL_DOWNSCALE_THRESHOLD_BYTES = 3 * 1024 * 1024; // 3 MB
const OCR_PREFILL_DOWNSCALE_MAX_EDGE = 1600;
const OCR_PREFILL_DOWNSCALE_JPEG_QUALITY = 0.85;

/**
 * Downscales an image file to at most `OCR_PREFILL_DOWNSCALE_MAX_EDGE`px on its
 * long edge and re-encodes as JPEG — a cédula photo only needs to stay legible
 * to OCR, not keep full phone-camera resolution. Falls back to the original
 * file if canvas isn't available or encoding fails.
 */
async function downscaleImage(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, OCR_PREFILL_DOWNSCALE_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', OCR_PREFILL_DOWNSCALE_JPEG_QUALITY)
  );
  if (!blob) return file;
  return new File([blob], file.name, { type: 'image/jpeg' });
}

/** Guards a file's MIME type against the shared allow-list before any upload. */
function assertAllowedDocType(file: File): void {
  if (!(ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
    throw new RequestError(400, 'UNSUPPORTED_TYPE', 'Tipo de archivo no permitido (usa PDF o imagen)');
  }
}

/**
 * Presign → browser PUT → register a file's bytes against an EXISTING document
 * record (`docId`). Shared by `uploadDocument` (new doc: create record first) and
 * `replaceDocument` (swap the file on an existing doc). The bytes go straight to
 * S3 from the browser — no AWS creds here (I6). Callers assert the MIME type first.
 */
async function putDocumentBlob(
  expedienteId: string,
  docId: string,
  file: File
): Promise<ExpedienteDoc> {
  const contentType = file.type;
  const { uploadUrl, s3Key } = await request<DocumentUploadUrlResponse>(
    'POST',
    `/api/v1/expedientes/${expedienteId}/documents/${docId}/upload-url`,
    { fileName: file.name, contentType }
  );
  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  });
  if (!put.ok) throw new RequestError(put.status, 'UPLOAD_FAILED', 'No se pudo subir el archivo');
  return toUiDocument(
    await request<ExpedienteDocumentResponse>(
      'POST',
      `/api/v1/expedientes/${expedienteId}/documents/${docId}/blob`,
      { s3Key, fileName: file.name, contentType, sizeBytes: file.size }
    )
  );
}

/**
 * Authenticated GET whose response is a raw file attachment (CSV/etc), not JSON.
 * Mirrors `request`'s auth-header handling but resolves to a `Blob` for the
 * caller to hand to `URL.createObjectURL`. Used by `downloadAuditReport`.
 */
async function requestBlob(path: string): Promise<Blob> {
  const requestId = crypto.randomUUID();
  const logPath = path.split('?')[0];
  let authHeaders: Record<string, string>;
  if (DEV_AUTH) {
    authHeaders = { 'x-dev-sub': DEV_SUB };
  } else {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      apiLog.warn('no session — request blocked', { method: 'GET', path: logPath, requestId });
      throw new RequestError(401, 'UNAUTHORIZED', 'No session');
    }
    authHeaders = { authorization: `Bearer ${token}` };
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: { ...authHeaders, 'x-request-id': requestId },
    });
  } catch (err) {
    apiLog.error('✗ network error', { method: 'GET', path: logPath, requestId });
    throw err;
  }

  if (!res.ok) {
    let code = 'UNKNOWN';
    let message = res.statusText;
    try {
      const data = (await res.json()) as ApiError;
      code = data.error.code;
      message = data.error.message;
    } catch {
      // non-JSON error body
    }
    apiLog.warn('✗ response', { method: 'GET', path: logPath, requestId, status: res.status, code });
    throw new RequestError(res.status, code, message);
  }

  apiLog.info('✓ response', { method: 'GET', path: logPath, requestId, status: res.status });
  return res.blob();
}

const realApi = {
  me: () => request<MeResponse>('GET', '/api/v1/me'),
  roles: () => request<RoleResponse[]>('GET', '/api/v1/roles'),
  listUsers: (params: { search?: string; status?: string } = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v) as [string, string][]
    ).toString();
    return request<UserResponse[]>('GET', `/api/v1/users${qs ? `?${qs}` : ''}`);
  },
  getUser: (id: string) => request<UserResponse>('GET', `/api/v1/users/${id}`),
  createUser: (body: { fullName: string; email: string; roleKeys: string[] }) =>
    request<UserResponse>('POST', '/api/v1/users', body),
  updateUser: (id: string, body: { fullName?: string; status?: string }) =>
    request<UserResponse>('PATCH', `/api/v1/users/${id}`, body),
  assignRoles: (id: string, roleKeys: string[]) =>
    request<UserResponse>('POST', `/api/v1/users/${id}/roles`, { roleKeys }),
  deactivateUser: (id: string) =>
    request<UserResponse>('POST', `/api/v1/users/${id}/deactivate`),
  reactivateUser: (id: string) =>
    request<UserResponse>('POST', `/api/v1/users/${id}/reactivate`),
  // Soft-delete (retention-safe). The backend only allows this once the user is
  // already `deactivated`; an active user returns 409 MUST_DEACTIVATE_FIRST.
  deleteUser: (id: string) => request<void>('DELETE', `/api/v1/users/${id}`),
  listTenants: () => request<TenantResponse[]>('GET', '/api/v1/admin/tenants'),
  createTenant: (body: {
    legalName: string;
    planTier?: string;
    admin: { email: string; fullName: string };
  }) => request<CreateTenantResponse>('POST', '/api/v1/admin/tenants', body),
  updateTenant: (id: string, body: { status?: 'active' | 'suspended' | 'offboarding'; planTier?: 'standard' | 'premium' }) =>
    request<TenantResponse>('PATCH', `/api/v1/admin/tenants/${encodeURIComponent(id)}`, body),
  // Wired to the real backend (Cumpli-dev). Map the phase1 UI shape <-> API shape.
  listExpedientes: async (): Promise<Expediente[]> =>
    (await request<ExpedienteResponse[]>('GET', '/api/v1/expedientes')).map(toUiExpediente),
  getExpediente: async (id: string): Promise<Expediente> =>
    toUiExpediente(await request<ExpedienteResponse>('GET', `/api/v1/expedientes/${id}`)),
  createExpediente: async (body: NewExpedienteInput): Promise<Expediente> =>
    toUiExpediente(
      await request<ExpedienteResponse>('POST', '/api/v1/expedientes', {
        persona: body.persona,
        nombre: body.name,
        docId: body.docId,
        sector: body.sector,
        kind: body.kind,
        ...(body.email ? { email: body.email } : {}),
        ...(body.phone ? { phone: body.phone } : {}),
        ...(body.dateOfBirth ? { dateOfBirth: body.dateOfBirth } : {}),
        ...(body.direccion ? { direccion: body.direccion } : {}),
        // Fase 3 — KYC registro: forward the typed entity kind, matrix inputs
        // (catalog keys) and the KYC core fields the wizard now captures.
        ...(body.entityType ? { entityType: body.entityType } : {}),
        ...(body.riskInputs ? { riskInputs: body.riskInputs } : {}),
        ...(body.proposito ? { proposito: body.proposito } : {}),
        ...(body.paisesActividad && body.paisesActividad.length > 0
          ? { paisesActividad: body.paisesActividad }
          : {}),
        ...(body.fuenteFondos ? { fuenteFondos: body.fuenteFondos } : {}),
        // §1 identity-by-folio (PJ) — already supported end-to-end, only the
        // wizard threading was missing.
        ...(body.folio ? { folio: body.folio } : {}),
        ...(body.constitutionDate ? { constitutionDate: body.constitutionDate } : {}),
        // PJ physical address + PN FATCA/CRS declarations. Booleans use
        // `!== undefined` so a declared `false` is persisted, not dropped.
        ...(body.direccionFisica ? { direccionFisica: body.direccionFisica } : {}),
        ...(body.usPerson !== undefined ? { usPerson: body.usPerson } : {}),
        ...(body.paisResidenciaFiscal ? { paisResidenciaFiscal: body.paisResidenciaFiscal } : {}),
        ...(body.multiplesNacionalidades !== undefined
          ? { multiplesNacionalidades: body.multiplesNacionalidades }
          : {}),
      })
    ),
  // OCR prefill for the intake wizard: read the cédula client-side, post it as
  // base64 JSON, and get back best-effort fields to pre-fill (stateless — the
  // backend persists nothing; the browser never talks to AWS directly — I6).
  ocrPrefill: async (file: File, kind: 'id' | 'address' = 'id'): Promise<OcrPrefillResult> => {
    const isImage = file.type.startsWith('image/');
    const upload =
      isImage && file.size > OCR_PREFILL_DOWNSCALE_THRESHOLD_BYTES ? await downscaleImage(file) : file;
    const bytes = new Uint8Array(await upload.arrayBuffer());
    // Encode in chunks — `String.fromCharCode(...allBytes)` blows the argument
    // stack on multi-MB phone photos.
    const CHUNK = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    const fileBase64 = btoa(binary);
    // PDFs can't be downscaled client-side, and a downscaled image can still
    // land over the cap (a very large/dense original) — fail fast without
    // ever calling the API, with a distinct error code the UI maps to
    // `intakeScan.tooLarge` instead of the generic OCR-failure message.
    if (fileBase64.length > OCR_PREFILL_MAX_FILE_BASE64_CHARS) {
      throw new RequestError(413, 'FILE_TOO_LARGE', 'El archivo es demasiado grande');
    }
    return request<OcrPrefillResult>('POST', '/api/v1/expedientes/ocr-prefill', {
      fileBase64,
      contentType: upload.type || 'application/octet-stream',
      kind,
    });
  },
  setExpedienteEstado: async (
    id: string,
    estado: EstadoExpediente,
    _action: string
  ): Promise<Expediente> =>
    toUiExpediente(
      await request<ExpedienteResponse>('PATCH', `/api/v1/expedientes/${id}`, { estado })
    ),

  // Full-field edit — PATCH accepts any subset of the widened update schema
  // (identity, classification, risk, assignment…). Maps the response back to the
  // UI shape so the detail screen can refresh in place.
  updateExpediente: async (id: string, patch: UpdateExpedienteInput): Promise<Expediente> =>
    toUiExpediente(await request<ExpedienteResponse>('PATCH', `/api/v1/expedientes/${id}`, patch)),

  // Soft-delete (retention-safe) — the row moves to the papelera, recoverable via
  // restoreExpediente.
  deleteExpediente: (id: string): Promise<void> =>
    request<void>('DELETE', `/api/v1/expedientes/${id}`),

  // Restore a soft-deleted expediente back to the active set.
  restoreExpediente: (id: string): Promise<void> =>
    request<void>('POST', `/api/v1/expedientes/${id}/restore`),

  // --- Expediente version projection ("Versiones" — Lane B, restorable) ---
  // Newest-first, cursor-paginated CQRS read-model of the audit stream; every
  // row is a full case-aggregate snapshot (expediente + its documents) and a
  // valid restore point. A read: no audit event.
  getExpedienteVersions: (id: string, cursor?: string): Promise<ExpedienteVersionsResponse> =>
    request<ExpedienteVersionsResponse>(
      'GET',
      `/api/v1/expedientes/${id}/versions${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`
    ),

  // --- Expediente activity log (the UNIFIED, restore-decorated timeline) ---
  // Every audited action across the expediente AND its documents/subjects/
  // owners/reviews/consents/alerts, newest first, cursor-paginated. Each entry
  // that has a paired `expediente_versions` projection row carries that row's
  // `versionSeq` + `restorable: true` (see `ExpedienteActivityEntry`). A read:
  // no audit event.
  getExpedienteActivity: (id: string, cursor?: string): Promise<ExpedienteActivityResponse> =>
    request<ExpedienteActivityResponse>(
      'GET',
      `/api/v1/expedientes/${id}/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`
    ),
  // A single version-projection row's full case-aggregate before/after
  // snapshot, by `versionSeq` — powers the drawer's "Expediente completo" tab
  // (fetched lazily, only when that tab opens). A read: no audit event.
  getExpedienteVersionDetail: (id: string, versionSeq: number): Promise<ExpedienteVersionDetailResponse> =>
    request<ExpedienteVersionDetailResponse>('GET', `/api/v1/expedientes/${id}/versions/${versionSeq}`),
  // Re-applies a prior version's full aggregate snapshot (incl. estado/
  // approval) as a NEW audited edit (never mutates the trail itself). The risk
  // score is always re-derived from riskInputs server-side — restoring does
  // NOT resurrect the old cached score.
  restoreExpedienteVersion: async (id: string, versionSeq: number): Promise<Expediente> =>
    toUiExpediente(
      await request<ExpedienteResponse>('POST', `/api/v1/expedientes/${id}/restore-version`, {
        versionSeq,
      })
    ),

  // Papelera — the trashed (soft-deleted) expedientes. Kept in the raw response
  // shape so the deletion timestamp (`deletedAt`) survives to the screen.
  listPapelera: (): Promise<PapeleraRow[]> =>
    request<PapeleraRow[]>('GET', '/api/v1/expedientes?deleted=true'),

  // --- AI decision log (super_admin supervisory read) ---
  listAssistantLog: (params: {
    tenantId: string;
    conversationId?: string;
    cursor?: string;
    limit?: number;
  }): Promise<AssistantLogListResponse> => {
    const qs = new URLSearchParams({ tenantId: params.tenantId });
    if (params.conversationId) qs.set('conversationId', params.conversationId);
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    return request<AssistantLogListResponse>('GET', `/api/v1/admin/assistant-log?${qs.toString()}`);
  },

  // --- Audit trail (append-only compliance log) ---
  listAudit: (
    params: {
      limit?: number;
      cursor?: string;
      entityType?: string;
      action?: string;
      from?: string;
      to?: string;
    } = {}
  ): Promise<AuditListResponse> => {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.entityType) qs.set('entityType', params.entityType);
    if (params.action) qs.set('action', params.action);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const query = qs.toString();
    return request<AuditListResponse>('GET', `/api/v1/audit${query ? `?${query}` : ''}`);
  },
  // The eventKey carries a `#` separator — URL-encode it into the path segment.
  getAuditEvidenceUrl: (eventKey: string): Promise<DocumentDownloadUrlResponse> =>
    request<DocumentDownloadUrlResponse>(
      'GET',
      `/api/v1/audit/${encodeURIComponent(eventKey)}/evidence-url`
    ),
  // CSV (default) or PDF (`format: 'pdf'`) export of the (filtered) audit log —
  // same auth/RBAC as `listAudit` (audit.list), returned as a raw Blob so the
  // caller can trigger a browser download without the response ever touching
  // JSON parsing. Both formats honor the SAME filters.
  downloadAuditReport: (
    params: {
      entityType?: string;
      action?: string;
      from?: string;
      to?: string;
      format?: 'csv' | 'pdf';
    } = {}
  ): Promise<Blob> => {
    const qs = new URLSearchParams();
    if (params.entityType) qs.set('entityType', params.entityType);
    if (params.action) qs.set('action', params.action);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.format === 'pdf') qs.set('format', 'pdf');
    const query = qs.toString();
    return requestBlob(`/api/v1/audit/export${query ? `?${query}` : ''}`);
  },

  // --- Expediente documents ---
  // `opts.deleted` fetches ONLY the soft-deleted (papelera) documents; absent
  // returns only the active ones. `opts.ownerId` (Fase 4 drill-down, req#7)
  // narrows to a single related person's documents; absent returns every doc of
  // the expediente (each carrying its `ownerId` so the entity view can label them).
  listDocuments: async (
    expedienteId: string,
    opts?: { deleted?: boolean; ownerId?: string }
  ): Promise<ExpedienteDocUi[]> => {
    const qs = new URLSearchParams();
    if (opts?.deleted) qs.set('deleted', 'true');
    if (opts?.ownerId) qs.set('ownerId', opts.ownerId);
    const q = qs.toString();
    return (
      await request<ExpedienteDocumentResponse[]>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/documents${q ? `?${q}` : ''}`
      )
    ).map(toUiDocument);
  },
  // `ownerId` (Fase 4) attaches the doc to a related person; absent = entity-level.
  addDocument: async (
    id: string,
    body: { name: string; category: string; ownerId?: string }
  ): Promise<ExpedienteDoc> =>
    toUiDocument(
      await request<ExpedienteDocumentResponse>('POST', `/api/v1/expedientes/${id}/documents`, {
        name: body.name,
        category: body.category,
        // Fase 3 drill-down — an ownerId ties this doc to a related person; absent =
        // entity-level document (the backend verifies same tenant+expediente).
        ...(body.ownerId ? { ownerId: body.ownerId } : {}),
      })
    ),
  // Upload a real file end to end: create the doc record, then presign → PUT → register
  // the blob (no AWS creds in the browser — I6). Shares `putDocumentBlob` with replace.
  // `input.ownerId` (Fase 4) scopes the new doc to a person's document slot.
  uploadDocument: async (
    expedienteId: string,
    input: { file: File; category: string; ownerId?: string }
  ): Promise<ExpedienteDoc> => {
    assertAllowedDocType(input.file);
    const doc = await realApi.addDocument(expedienteId, {
      name: input.file.name,
      category: input.category,
      ...(input.ownerId ? { ownerId: input.ownerId } : {}),
    });
    return putDocumentBlob(expedienteId, doc.id, input.file);
  },
  // Replace = attach a NEW file to an EXISTING doc record: skip the create step and
  // reuse the upload internals (presign → PUT → register) against the same docId.
  replaceDocument: async (
    expedienteId: string,
    docId: string,
    file: File
  ): Promise<ExpedienteDoc> => {
    assertAllowedDocType(file);
    return putDocumentBlob(expedienteId, docId, file);
  },
  // Soft-delete a single document (retention-safe) — recoverable via restoreDocument.
  deleteDocument: (expedienteId: string, docId: string): Promise<void> =>
    request<void>('DELETE', `/api/v1/expedientes/${expedienteId}/documents/${docId}`),
  // Restore a soft-deleted document back to the active set.
  restoreDocument: (expedienteId: string, docId: string): Promise<void> =>
    request<void>('POST', `/api/v1/expedientes/${expedienteId}/documents/${docId}/restore`),
  updateDocument: async (
    expedienteId: string,
    docId: string,
    patch: ExpedienteDocPatch
  ): Promise<ExpedienteDoc> =>
    toUiDocument(
      await request<ExpedienteDocumentResponse>(
        'PATCH',
        `/api/v1/expedientes/${expedienteId}/documents/${docId}`,
        patch
      )
    ),
  getDocumentDownloadUrl: (expedienteId: string, docId: string) =>
    request<DocumentDownloadUrlResponse>(
      'GET',
      `/api/v1/expedientes/${expedienteId}/documents/${docId}/download-url`
    ),

  // --- Validations (POST /validations/{key}/run, GET /validations) ---
  runValidation: async (key: string, body: RunValidationBody): Promise<ValidationRun> =>
    toUiValidationRun(
      await request<ValidationResult>('POST', `/api/v1/validations/${key}/run`, body)
    ),
  listValidationRuns: async (subjectId: string): Promise<ValidationRun[]> =>
    (
      await request<ValidationResult[]>(
        'GET',
        `/api/v1/validations?subjectId=${encodeURIComponent(subjectId)}`
      )
    ).map(toUiValidationRun),

  // Sends a document-request notification (WhatsApp/email). The backend applies
  // the recipient override, so every send is routed to the configured test
  // phone/email regardless of the client's stored contact.
  requestDocuments: (id: string, body: { channels: string[]; message?: string }) =>
    request<DocumentRequestResponse>('POST', `/api/v1/expedientes/${id}/request-documents`, body),

  // --- Batch sanctions screening (mass list validation) ---
  screenBatch: (items: { name: string; country?: string }[]): Promise<ScreeningBatchResponse> =>
    request<ScreeningBatchResponse>('POST', '/api/v1/screening/batch', { items }),

  // --- Registro Público de Panamá (consulta on-demand de un folio) ---
  // Delegates to the rp-consulta service through the API (folio/name lookup →
  // vigencia, miembros, prelación, cotejo). The browser never scrapes or holds
  // credentials — it only forwards the query (I6/I7).
  rpConsulta: (input: RpConsultaInput): Promise<RpConsultaResult> =>
    request<RpConsultaResult>('POST', '/api/v1/registro-publico/consulta', input),

  // --- Vigilancia Registral: watchlist de folios + chequeo semanal ---
  // Marca folios para revisión periódica; el chequeo semanal levanta alertas
  // (que aparecen en Alertas y Casos) cuando un folio vigilado cambia.
  rpWatchList: (): Promise<RpWatch[]> =>
    request<RpWatch[]>('GET', '/api/v1/registro-publico/watch'),
  rpWatchAdd: (input: RpConsultaInput): Promise<RpWatch> =>
    request<RpWatch>('POST', '/api/v1/registro-publico/watch', input),
  rpWatchRemove: (id: string): Promise<RpWatch[]> =>
    request<RpWatch[]>('POST', `/api/v1/registro-publico/watch/${id}/remove`),
  rpRunWeeklyCheck: (): Promise<RpWeeklyCheckResult> =>
    request<RpWeeklyCheckResult>('POST', '/api/v1/registro-publico/watch/run'),

  // --- Vigilancia Registral: login RP + descarga por lote (proxy a rp-consulta) ---
  // El usuario digita SUS credenciales del Registro Público; el navegador solo las
  // reenvía en el login y nunca las guarda (I6/I7). El servicio hace el scraping.
  rpSalud: (): Promise<RpSalud> =>
    request<RpSalud>('GET', '/api/v1/registro-publico/salud'),
  rpLogin: (input: RpLoginInput): Promise<{ ok: boolean; sesion_guardada?: boolean }> =>
    request('POST', '/api/v1/registro-publico/login', input),
  // Barrer mi cartera: el servicio abre el folio de cada expediente en el
  // Registro Público, detecta cambios y levanta alertas (que aparecen en Alertas).
  rpSweep: (): Promise<RpSweepResult> =>
    request<RpSweepResult>('POST', '/api/v1/registro-publico/sweep'),

  // --- AI assistant (scoped, data-backed actions) ---
  assistantExplainRisk: (expedienteId: string): Promise<AssistantResponse> =>
    request<AssistantResponse>('POST', '/api/v1/assistant', {
      skill: 'explain_risk',
      expedienteId,
    }),
  // The chat turn may now carry `proposedActions` (write actions the copilot
  // suggests). They flow through untouched via the shared response type; the
  // Asistente panel surfaces them as confirm cards (propose → confirm → execute).
  assistantChat: (
    message: string,
    expedienteId?: string,
    history?: Array<{ role: 'user' | 'assistant'; text: string }>,
    conversationId?: string,
    signal?: AbortSignal
  ): Promise<AssistantChatResponse> =>
    request<AssistantChatResponse>(
      'POST',
      '/api/v1/assistant/chat',
      {
        message,
        ...(expedienteId ? { expedienteId } : {}),
        ...(history && history.length > 0 ? { history } : {}),
        ...(conversationId ? { conversationId } : {}),
      },
      signal
    ),
  // Confirm + run a copilot-proposed write action. The backend re-validates the
  // tool + input (deny-by-default: 400 unknown/non-write tool or bad input, 403
  // for read-only roles like auditor, 429 when rate-limited) and emits its own
  // audit event — the browser only forwards the reviewed payload. `opts` links
  // the confirmation to its proposal in the AI decision log (B3): `conversationId`
  // + `turnId` correlate to the chat turn that proposed the action; `edited` is
  // the anti-rubber-stamp signal (true only when the user changed the proposal
  // before confirming).
  assistantExecute: (
    tool: string,
    input: Record<string, unknown>,
    opts?: {
      conversationId?: string | undefined;
      turnId?: string | undefined;
      edited?: boolean | undefined;
    }
  ): Promise<AssistantExecuteResponse> =>
    request<AssistantExecuteResponse>('POST', '/api/v1/assistant/execute', {
      tool,
      input,
      ...(opts?.conversationId ? { conversationId: opts.conversationId } : {}),
      ...(opts?.turnId ? { turnId: opts.turnId } : {}),
      ...(opts?.edited !== undefined ? { edited: opts.edited } : {}),
    }),
  // The copilot's browsable capability catalog (explore cards + "/" menu). The
  // server RBAC-filters this per caller, so the UI renders EXACTLY what comes
  // back — it never re-filters by role client-side.
  getAssistantTools: (): Promise<AssistantToolsResponse> =>
    request<AssistantToolsResponse>('GET', '/api/v1/assistant/tools'),

  onboardingTrend: async (): Promise<{ points: number[]; labels: string[] }> => {
    const exps = await request<ExpedienteResponse[]>('GET', '/api/v1/expedientes');
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const points: number[] = [];
    const labels: string[] = [];
    for (let i = 7; i >= 0; i--) {
      const endMs = now - i * WEEK;
      const startMs = endMs - WEEK;
      points.push(
        exps.filter((e) => {
          const t = new Date(e.startedAt).getTime();
          return !isNaN(t) && t >= startMs && t < endMs;
        }).length
      );
      // Label each week by the day it ends so the latest bucket reads as the
      // current week (aligns with the "esta semana" delta badge).
      labels.push(
        new Date(endMs - 1).toLocaleDateString('es-PA', { day: '2-digit', month: 'short' })
      );
    }
    return { points, labels };
  },

  // --- Risk matrix (configurable factor weights) ---
  getRiskMatrix: (): Promise<RiskMatrixResponse> =>
    request<RiskMatrixResponse>('GET', '/api/v1/risk-matrix'),
  saveRiskMatrix: (body: RiskMatrixConfig): Promise<RiskMatrixResponse> =>
    request<RiskMatrixResponse>('POST', '/api/v1/risk-matrix', body),

  // --- Tenant settings (§2: firm mode drives which risk indicators apply) ---
  getTenantSettings: (): Promise<TenantSettingsResponse> =>
    request<TenantSettingsResponse>('GET', '/api/v1/tenant-settings'),
  updateTenantSettings: (body: { entityMode: EntityMode }): Promise<TenantSettingsResponse> =>
    request<TenantSettingsResponse>('PATCH', '/api/v1/tenant-settings', body),

  // --- Alerts ---
  listAlerts: async (): Promise<Alert[]> =>
    (await request<AlertResponse[]>('GET', '/api/v1/alertas')).map(toUiAlert),
  getAlert: async (id: string): Promise<Alert> =>
    toUiAlert(await request<AlertResponse>('GET', `/api/v1/alertas/${id}`)),
  updateAlert: async (id: string, patch: AlertPatch): Promise<Alert> =>
    toUiAlert(await request<AlertResponse>('PATCH', `/api/v1/alertas/${id}`, patch)),

  // --- Renewals (§3 debida-diligencia renewal alarms) ---
  // Lists expedientes whose por-riesgo review is due (or within `windowDays`),
  // and runs the on-demand scan that opens renovación alerts (tipo
  // ALERT_TYPE_RENOVACION) for the due ones. The scan is gated server-side to
  // the renewal.run roles; the browser only forwards the request.
  listDueRenewals: (windowDays?: number): Promise<DueRenewalResponse[]> =>
    request<DueRenewalResponse[]>(
      'GET',
      `/api/v1/renovaciones/due${windowDays !== undefined ? `?windowDays=${windowDays}` : ''}`
    ),
  runRenewals: (): Promise<RunRenewalsResponse> =>
    request<RunRenewalsResponse>('POST', '/api/v1/renovaciones/run'),

  // --- Subjects (subject of an expediente) ---
  listSubjects: async (expedienteId: string): Promise<Subject[]> =>
    (
      await request<SubjectResponse[]>('GET', `/api/v1/expedientes/${expedienteId}/subjects`)
    ).map(toUiSubject),
  createSubject: async (expedienteId: string, body: CreateSubjectInput): Promise<Subject> =>
    toUiSubject(
      await request<SubjectResponse>('POST', `/api/v1/expedientes/${expedienteId}/subjects`, body)
    ),
  getSubject: async (expedienteId: string, subjectId: string): Promise<Subject> =>
    toUiSubject(
      await request<SubjectResponse>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/subjects/${subjectId}`
      )
    ),
  updateSubject: async (
    expedienteId: string,
    subjectId: string,
    patch: SubjectPatch
  ): Promise<Subject> =>
    toUiSubject(
      await request<SubjectResponse>(
        'PATCH',
        `/api/v1/expedientes/${expedienteId}/subjects/${subjectId}`,
        patch
      )
    ),

  // --- Owners (ownership/control graph — UBO) ---
  // list/get return the widened OwnerUi (Fase 1 per-person fields the persona
  // drill-down renders); create/update keep the base Owner (nothing reads the
  // extra fields off a mutation result).
  listOwners: async (expedienteId: string): Promise<OwnerUi[]> =>
    (
      await request<OwnerResponse[]>('GET', `/api/v1/expedientes/${expedienteId}/owners`)
    ).map(toUiOwner),
  createOwner: async (expedienteId: string, body: CreateOwnerInput): Promise<Owner> =>
    toUiOwner(
      await request<OwnerResponse>('POST', `/api/v1/expedientes/${expedienteId}/owners`, body)
    ),
  getOwner: async (expedienteId: string, ownerId: string): Promise<OwnerUi> =>
    toUiOwner(
      await request<OwnerResponse>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/owners/${ownerId}`
      )
    ),
  updateOwner: async (
    expedienteId: string,
    ownerId: string,
    patch: OwnerPatch
  ): Promise<Owner> =>
    toUiOwner(
      await request<OwnerResponse>(
        'PATCH',
        `/api/v1/expedientes/${expedienteId}/owners/${ownerId}`,
        patch
      )
    ),

  // --- Registro Público officers (directores/dignatarios/apoderados sincronizados
  // del barrido RP) — comparados contra los dueños declarados para detectar
  // testaferros; solo lectura desde el frontend, se sincronizan por el barrido.
  listRegistryOfficers: (expedienteId: string): Promise<RegistryOfficerResponse[]> =>
    request<RegistryOfficerResponse[]>(
      'GET',
      `/api/v1/expedientes/${expedienteId}/registry-officers`
    ),

  // --- Vínculos (reverse ownership: entidades donde este expediente es UBO) ---
  // Filas de subject_owners con kyc_expediente_id = {id}, unidas al expediente
  // padre. RBAC reutiliza la acción de listar owners (ver contrato del backend).
  getVinculos: (expedienteId: string): Promise<VinculoResponse[]> =>
    request<VinculoResponse[]>('GET', `/api/v1/expedientes/${expedienteId}/vinculos`),

  // --- Reviews (periodic re-review of an expediente) ---
  listReviews: async (expedienteId: string): Promise<Review[]> =>
    (
      await request<ReviewResponse[]>('GET', `/api/v1/expedientes/${expedienteId}/reviews`)
    ).map(toUiReview),
  createReview: async (expedienteId: string, body: CreateReviewInput): Promise<Review> =>
    toUiReview(
      await request<ReviewResponse>('POST', `/api/v1/expedientes/${expedienteId}/reviews`, body)
    ),
  getReview: async (expedienteId: string, reviewId: string): Promise<Review> =>
    toUiReview(
      await request<ReviewResponse>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/reviews/${reviewId}`
      )
    ),
  updateReview: async (
    expedienteId: string,
    reviewId: string,
    patch: ReviewPatch
  ): Promise<Review> =>
    toUiReview(
      await request<ReviewResponse>(
        'PATCH',
        `/api/v1/expedientes/${expedienteId}/reviews/${reviewId}`,
        patch
      )
    ),

  // --- Credit consents (APC/credit-bureau consent on file) ---
  listConsents: async (expedienteId: string): Promise<Consent[]> =>
    (
      await request<ConsentResponse[]>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/credit-consents`
      )
    ).map(toUiConsent),
  createConsent: async (expedienteId: string, body: CreateConsentInput): Promise<Consent> =>
    toUiConsent(
      await request<ConsentResponse>(
        'POST',
        `/api/v1/expedientes/${expedienteId}/credit-consents`,
        body
      )
    ),
  getConsent: async (expedienteId: string, consentId: string): Promise<Consent> =>
    toUiConsent(
      await request<ConsentResponse>(
        'GET',
        `/api/v1/expedientes/${expedienteId}/credit-consents/${consentId}`
      )
    ),
  updateConsent: async (
    expedienteId: string,
    consentId: string,
    patch: ConsentPatch
  ): Promise<Consent> =>
    toUiConsent(
      await request<ConsentResponse>(
        'PATCH',
        `/api/v1/expedientes/${expedienteId}/credit-consents/${consentId}`,
        patch
      )
    ),

  // --- Alcance a clientes (outreach: suggested/scheduled/sent messages) ---
  generateOutreach: (): Promise<OutreachListResponse> =>
    request<OutreachListResponse>('POST', '/api/v1/alcance/generar'),
  listOutreach: (status?: string): Promise<OutreachListResponse> =>
    request<OutreachListResponse>(
      'GET',
      `/api/v1/alcance${status ? `?status=${encodeURIComponent(status)}` : ''}`
    ),
  updateOutreach: (id: string, body: UpdateOutreachInput): Promise<OutreachMessageResponse> =>
    request<OutreachMessageResponse>('PATCH', `/api/v1/alcance/${id}`, body),
  sendOutreach: (id: string): Promise<OutreachMessageResponse> =>
    request<OutreachMessageResponse>('POST', `/api/v1/alcance/${id}/enviar`),
  cancelOutreach: (id: string): Promise<OutreachMessageResponse> =>
    request<OutreachMessageResponse>('POST', `/api/v1/alcance/${id}/cancelar`),
  runOutreach: (): Promise<RunOutreachResponse> =>
    request<RunOutreachResponse>('POST', '/api/v1/alcance/run'),
};

// DEMO MODE (VITE_DEMO=true): swap the network client for an in-memory mock so
// the UI is navigable without Cognito/API/DB. No-op in normal builds.
// `satisfies typeof realApi` enforces structural parity at compile time, so a
// missing method or a signature drift in demoApi fails `pnpm typecheck` rather
// than crashing at runtime in VITE_DEMO=true.
const demoClient = demoApi satisfies typeof realApi;
export const api: typeof realApi = DEMO ? demoClient : realApi;
